import {
  readEstimateMoneyV2,
  readHistoryMoneyV2,
  readPaymentMoneyV2,
  type EstimateMoneyV2,
  type HistoryMoneyV2,
  type PaymentMoneyV2,
  type ProjectionErrorCode,
} from "@voltie/localization-web";
import type { DurationBreakdown, DurationIssue, PaymentInfo } from "@/lib/api";

// GET /api/unit/<id>/sessions with money contract 2 (route family F1): the unit's current
// session and a page of its history plus "money_contract": 2. Money is read from the
// money_v2 siblings only: the current session's costs.money_v2 (estimate_money_v2), each
// row's costs.money_v2 (history_money_v2) and its payment.money_v2 (payment_money_v2).
// The legacy money keys beside them are never read for an amount, not even for USD, and
// never returned. A 200 from a server that predates F1 has no marker and is invalid as a
// whole page. A row whose money cannot be read keeps its other fields; only its money is
// invalid. The one exception to legacy keys is D-T2-1: a legacy USD row without a v2
// invoice keeps its retained line_items as its breakdown.

/** Legacy money keys of the current session root (legacy USD only). */
const CURRENT_LEGACY_MONEY_KEYS = ["authorized_amount", "cost_estimate_cents", "hold_amount_cents"] as const;

/** Legacy money keys of a payment object (legacy USD only). */
const PAYMENT_LEGACY_MONEY_KEYS = ["billed_amount", "captured_payment", "amount_due", "authorized_hold_amount", "outstanding_amount"] as const;

type PaymentLegacyMoneyKey = (typeof PAYMENT_LEGACY_MONEY_KEYS)[number];

/** The non-money payment facts of a row: state, attention and admin diagnostics. */
export type PaymentFacts = Omit<PaymentInfo, PaymentLegacyMoneyKey>;

/** One retained legacy line item (backend-computed; amount_cents are hundredths of the major unit in every currency). */
export interface LegacyLineItem {
  readonly start_at?: string | null;
  readonly end_at?: string | null;
  readonly fee_type: string;
  readonly uom: string;
  /** Major units of the row currency per uom. */
  readonly rate: number;
  readonly amount_cents: number;
  readonly billable?: boolean;
  readonly energy_kwh?: number;
  readonly hours?: number;
}

export type UnitSessionsErrorCode = ProjectionErrorCode | "money_contract" | "field" | "outstanding_known";

interface InvalidRead {
  readonly status: "invalid";
  readonly error: UnitSessionsErrorCode;
  readonly path: string;
  readonly detail: string;
}

/** The live estimate of the current session; "none" when the session carries no money record. */
export type CurrentEstimateRead = { readonly status: "value"; readonly value: EstimateMoneyV2 } | { readonly status: "none" } | InvalidRead;

/** The money of one history row: its costs block, its payment block (null without a payment object) and the D-T2-1 legacy USD lines. */
export type SessionMoneyRead =
  | {
      readonly status: "value";
      readonly history: HistoryMoneyV2;
      readonly payment: PaymentMoneyV2 | null;
      /** Retained legacy line_items of a USD row without a v2 invoice; null otherwise. */
      readonly legacy_usd_lines: readonly LegacyLineItem[] | null;
    }
  | InvalidRead;

/** A current session without its legacy money keys; the estimate replaces costs. */
export interface CurrentSession {
  readonly session_id?: string;
  readonly start_time?: string | null;
  readonly duration_seconds?: number | null;
  readonly energy_delivered?: number | null;
  readonly subsessions_count?: number;
  readonly subsessions?: readonly unknown[];
  readonly estimate: CurrentEstimateRead;
  readonly [key: string]: unknown;
}

/** A history row without its legacy money keys; money replaces costs. */
export interface UnitSessionRow {
  readonly session_id: string;
  readonly start_time?: string | null;
  readonly end_time?: string | null;
  readonly duration_seconds?: number;
  // Charging time only (idle/suspended excluded). Always a number in the published
  // contract; whether it can be trusted is duration_breakdown.
  readonly charging_seconds?: number;
  // Additive split fields (null = never recorded). Absent on payloads from a backend
  // that predates the split; those keep the original two timing fields only.
  readonly idle_seconds?: number | null;
  readonly suspended_seconds?: number | null;
  readonly duration_breakdown?: DurationBreakdown;
  readonly duration_issues?: DurationIssue[];
  readonly energy_delivered?: number; // Wh
  readonly customer_name?: string | null;
  readonly customer_email?: string | null;
  readonly authorized_by?: string;
  readonly ended_by?: string | null;
  readonly pricing_profile_name?: string | null;
  readonly pricing_source?: string | null;
  readonly billing_source?: string;
  // The session row's billing_status (pending | finalizing | failed | finalized |
  // not_authorized | null for legacy).
  readonly billing_status?: string | null;
  readonly subsessions_count?: number;
  readonly subsessions?: readonly unknown[];
  readonly money: SessionMoneyRead;
  // Payment status projected the same way as the admin org sessions report. Absent when
  // the payment projection failed (payment_available false) or on legacy sessions.
  readonly payment?: PaymentFacts;
}

export interface UnitSessionsPage {
  readonly unit_id: string;
  readonly unit_name: string;
  readonly current_session: CurrentSession | null;
  readonly session_history: readonly UnitSessionRow[];
  readonly history_has_more: boolean;
  readonly history_next_offset: number | null;
  // False when the payment projection failed to load for this response; the UI shows
  // "Unavailable" (financial-data error) rather than "—" (no payment facts). Absent when
  // history wasn't requested.
  readonly payment_available?: boolean;
}

export type UnitSessionsRead = { readonly status: "value"; readonly value: UnitSessionsPage } | InvalidRead;

const show = (value: unknown) => (typeof value === "string" ? JSON.stringify(value) : String(value));
const invalid = (error: UnitSessionsErrorCode, path: string, detail: string): InvalidRead => ({ status: "invalid", error, path, detail });
const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null && !Array.isArray(value);

function without(record: Record<string, unknown>, keys: readonly string[]): Record<string, unknown> {
  const copy = { ...record };
  for (const key of keys) delete copy[key];
  return copy;
}

function readEstimate(costs: unknown, path: string): CurrentEstimateRead {
  const block = isRecord(costs) ? costs.money_v2 : undefined;
  const read = readEstimateMoneyV2(block);
  if (read.status === "unavailable") return { status: "none" };
  if (read.status === "invalid") return invalid(read.error, read.path ? `${path}.${read.path}` : path, read.detail);
  return read;
}

const optional = (value: unknown, type: "string" | "number" | "boolean") => value === undefined || typeof value === type;

function legacyLine(input: unknown): input is LegacyLineItem {
  if (!isRecord(input)) return false;
  return (
    typeof input.fee_type === "string" &&
    typeof input.uom === "string" &&
    typeof input.rate === "number" &&
    Number.isFinite(input.rate) &&
    Number.isSafeInteger(input.amount_cents) &&
    (input.start_at === null || optional(input.start_at, "string")) &&
    (input.end_at === null || optional(input.end_at, "string")) &&
    optional(input.billable, "boolean") &&
    optional(input.energy_kwh, "number") &&
    optional(input.hours, "number")
  );
}

/** D-T2-1: only a USD row without a v2 invoice may show the line_items the server retained (MV-7); malformed lines are no breakdown. */
function legacyUsdLines(costs: Record<string, unknown>, history: HistoryMoneyV2): readonly LegacyLineItem[] | null {
  if (history.invoice !== null || history.currency !== "USD") return null;
  const lines = costs.line_items;
  if (!Array.isArray(lines) || !lines.every(legacyLine)) return null;
  return Object.freeze(lines.map((line) => Object.freeze({ ...line })));
}

function readRowMoney(row: Record<string, unknown>, path: string): SessionMoneyRead {
  const costs = row.costs;
  if (!isRecord(costs)) return invalid("not_object", `${path}.costs`, show(costs));
  const history = readHistoryMoneyV2(costs.money_v2);
  if (history.status === "unavailable") return invalid("missing_field", `${path}.costs.money_v2`, "money_v2");
  if (history.status === "invalid") return invalid(history.error, `${path}.costs.money_v2${history.path ? `.${history.path}` : ""}`, history.detail);

  let payment: PaymentMoneyV2 | null = null;
  if (row.payment !== undefined) {
    const at = `${path}.payment`;
    if (!isRecord(row.payment)) return invalid("not_object", at, show(row.payment));
    const read = readPaymentMoneyV2(row.payment.money_v2);
    if (read.status === "unavailable") return invalid("missing_field", `${at}.money_v2`, "money_v2");
    if (read.status === "invalid") return invalid(read.error, `${at}.money_v2${read.path ? `.${read.path}` : ""}`, read.detail);
    const known = row.payment.outstanding_known;
    if (typeof known !== "boolean") return invalid("outstanding_known", `${at}.outstanding_known`, show(known));
    if (!known && read.value.outstanding !== null) {
      return invalid("outstanding_known", `${at}.money_v2.outstanding`, "an amount while outstanding_known is false");
    }
    payment = read.value;
  }
  return { status: "value", history: history.value, payment, legacy_usd_lines: legacyUsdLines(costs, history.value) };
}

type Part<T> = { readonly ok: true; readonly value: T } | { readonly ok: false; readonly error: InvalidRead };

function readRow(input: unknown, path: string): Part<UnitSessionRow> {
  if (!isRecord(input)) return { ok: false, error: invalid("not_object", path, show(input)) };
  if (typeof input.session_id !== "string") return { ok: false, error: invalid("field", `${path}.session_id`, show(input.session_id)) };
  const money = readRowMoney(input, path);
  const row = without(input, ["costs", "payment"]);
  if (isRecord(input.payment)) row.payment = Object.freeze(without(input.payment, [...PAYMENT_LEGACY_MONEY_KEYS, "money_v2"]));
  row.money = money;
  return { ok: true, value: Object.freeze(row) as unknown as UnitSessionRow };
}

function readCurrent(input: unknown): Part<CurrentSession | null> {
  if (input === null || input === undefined) return { ok: true, value: null };
  if (!isRecord(input)) return { ok: false, error: invalid("not_object", "current_session", show(input)) };
  const current = without(input, [...CURRENT_LEGACY_MONEY_KEYS, "costs"]);
  current.estimate = readEstimate(input.costs, "current_session.costs.money_v2");
  return { ok: true, value: Object.freeze(current) as unknown as CurrentSession };
}

export function readUnitSessionsPage(body: unknown): UnitSessionsRead {
  if (!isRecord(body)) return invalid("not_object", "", show(body));
  if (body.money_contract !== 2) return invalid("money_contract", "money_contract", show(body.money_contract));
  if (typeof body.unit_id !== "string") return invalid("field", "unit_id", show(body.unit_id));
  const next = body.history_next_offset ?? null;
  if (next !== null && (typeof next !== "number" || !Number.isSafeInteger(next) || next < 0)) return invalid("field", "history_next_offset", show(next));
  const more = body.history_has_more ?? false;
  if (typeof more !== "boolean") return invalid("field", "history_has_more", show(more));
  if (body.payment_available !== undefined && typeof body.payment_available !== "boolean") {
    return invalid("field", "payment_available", show(body.payment_available));
  }
  const rawHistory = body.session_history ?? [];
  if (!Array.isArray(rawHistory)) return invalid("not_array", "session_history", show(rawHistory));

  // The current-session and row objects themselves must be readable; their money is isolated in estimate and money.
  const current = readCurrent(body.current_session);
  if (!current.ok) return current.error;
  const rows: UnitSessionRow[] = [];
  for (const [i, entry] of rawHistory.entries()) {
    const row = readRow(entry, `session_history[${i}]`);
    if (!row.ok) return row.error;
    rows.push(row.value);
  }

  const page: UnitSessionsPage = {
    unit_id: body.unit_id,
    unit_name: typeof body.unit_name === "string" ? body.unit_name : "",
    current_session: current.value,
    session_history: Object.freeze(rows),
    history_has_more: more,
    history_next_offset: next,
    ...(body.payment_available === undefined ? {} : { payment_available: body.payment_available as boolean }),
  };
  return { status: "value", value: Object.freeze(page) };
}
